import { context } from '@shared-services/context-service';
import { localizationService } from '@shared-services/localization-service';
import * as filestack from 'client/widget-components/services/filestack';
import { useCallback, useEffect, useState } from 'react';
import { getWindowInBrowser } from '../../../RuntimeWindow';
import {
    DEFAULT_MAX_SIZE,
    DEFAULT_MIME_TYPES,
    FILE_STACK_DEFAULT_SOURCES,
    DEFAULT_UPLOAD_IN_BACKGROUND,
} from './consts';
import { UploadResult } from './types';

// https://filestack.github.io/filestack-js/interfaces/PickerOptions.html
const SUPPORTED_FILESTACK_LANGS = new Set([
    'ca', 'da', 'de', 'en', 'es', 'fr', 'he', 'it', 'ja', 'ko',
    'nl', 'no', 'pl', 'pt', 'sv', 'ru', 'vi', 'zh', 'tr',
]);

function getSupportedFilestackLang(): string {
    const locale = getWindowInBrowser()?.dmAPI.getSiteCurrentLocale();
    const lang = locale?.split('-')[0];
    return lang && SUPPORTED_FILESTACK_LANGS.has(lang) ? lang : 'en';
}

interface UseFileUploadProps {
    apiKey: string;
    pickerOptions?: Partial<filestack.PickerOptions>;
    onUploadSuccess: (result: UploadResult[]) => void;
    onUploadError?: (error: Error) => void;
}

export function useFilestack({
    apiKey,
    pickerOptions = {},
    onUploadSuccess,
    onUploadError,
}: UseFileUploadProps) {
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        filestack.init();
    }, []);

    const handlePickAndStore = useCallback(async () => {
        if (context.isEditor) {
            return;
        }
        setLoading(true);
        try {
            const options: filestack.PickerOptions = {
                lang: getSupportedFilestackLang(),
                // Following attributes can be overridden by pickerOptions
                maxSize: DEFAULT_MAX_SIZE,
                accept: DEFAULT_MIME_TYPES,
                fromSources: FILE_STACK_DEFAULT_SOURCES,
                uploadInBackground: DEFAULT_UPLOAD_IN_BACKGROUND,
                maxFiles: 100,
                ...pickerOptions,
            };

            const response = await filestack.picker(apiKey, options);

            const results = response.filesUploaded.map((file) => ({
                url: file.url,
                filename: file.filename,
                size: file.size,
                mimetype: file.mimetype,
                handle: file.handle,
                key: file.key,
                uploadedAt: new Date(),
            }));

            onUploadSuccess(results);
        } catch (err) {
            const errorMessage =
                localizationService.str(
                    'ui.runtimessr.fileupload.error.upload_failed'
                ) || 'Upload failed';
            onUploadError?.(new Error(errorMessage));
        } finally {
            setLoading(false);
        }
    }, [apiKey, pickerOptions, onUploadSuccess, onUploadError]);

    return {
        loading,
        handlePickAndStore,
    };
}
